import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Plus, Edit, Trash2, ArrowLeft, ArrowRight } from 'lucide-react';
import useModal from '../../../../../../hooks/modalHook';
import useUserAccount from '../../../../../../hooks/userAccountHook';
import CreatePolicyFleetVehicleModal from './CreatePolicyFleetVehicleForm';
import Button from '../../../../../../components/form-control/Button';
import FileUpload from '../../../../../../components/elements/FileUpload/FileUpload';
import Loader from '../../../../../../components/Loader';
import GTable from '../../../../../../components/elements/GTable/GTable';
import { FleetVehicle } from '../index';
import {
  bodyTypeMappingFromNumberToString,
  formatCurrencyAmount,
  getPhoneNumberFromShortCodeFormat,
  onDownloadFileFromUrl,
  showToast,
  usageMapToString
} from '../../../../../../utils/miscFunctions';
import ActionConfirmation from '../../../../../../components/elements/ActionConfirmation';
import { useSelector } from 'react-redux';
import { getQuoteWidgetPayloadFromState } from '../../../../../../states/slices/quoteWidgetSlice';

interface CreatePolicyFleetProps {
  vehicles: FleetVehicle[];
  fleetCSV?: File | null;
  onVehiclesUpdate: (vehicles: FleetVehicle[], csvFile?: File) => void;
  onNext: () => void;
  onPrevious: () => void;
  data: any;
}

const PolicyFleetCreationPage: React.FC<CreatePolicyFleetProps> = ({
  vehicles: initialVehicles,
  fleetCSV: initialFleetCSV,
  onVehiclesUpdate,
  onNext,
  onPrevious,
  data
}) => {
  const navigate = useNavigate();
  const { closeModal, updateModalStates, ModalComponent } = useModal();
  const [fleetCSV, setFleetCSV] = useState<File | undefined>(initialFleetCSV || undefined);
  const [currentStep, setCurrentStep] = useState(1);
  const { user } = useUserAccount();
  const dataFromQuoteCalculation = useSelector(getQuoteWidgetPayloadFromState);
  const templateUrl = '/documents/FleetTemplate-Policy.csv';
  // const templateUrl = 'https://docs.google.com/spreadsheets/d/1Nnq8z2rtN7WR2ouV27zuJO8_ai-pnmxltUhMmEWs5AA/edit?gid=0#gid=0';
  const templateName = "FleetTemplate-Policy.csv"

  // Initialize state with props
  const [vehicles, setVehicles] = useState<FleetVehicle[]>(initialVehicles);

  // Loading states
  const [isUploading, setIsUploading] = useState(false);

  const columns = useMemo(
    () => [
      // Vehicle Information Group
      {
        Header: 'Vehicle Information',
        columns: [
          {
            Header: 'Make',
            accessor: 'maker',
            minWidth: 120
          },
          {
            Header: 'Model',
            accessor: 'model',
            minWidth: 120
          },
          {
            Header: 'Year',
            accessor: 'yearIssued',
            Cell: ({ value }: { value: number }) => value || 'N/A',
            width: 80
          },
          {
            Header: 'Body Type',
            accessor: 'vehicleBodyType',
            minWidth: 120,
            Cell: ({ value }: { value: string }) => {
              return bodyTypeMappingFromNumberToString[value] || value || '-';
            }
          },
          {
            Header: 'Color',
            accessor: 'color',
            minWidth: 100
          },
          {
            Header: 'CC',
            accessor: 'cc',
            Cell: ({ value }: { value: number }) => (value ? `${value.toLocaleString()}` : 'N/A'),
            width: 80
          },
          {
            Header: 'Seats',
            accessor: 'maxSeat',
            width: 70
          }
        ]
      },
      // Registration & Identification Group
      {
        Header: 'Registration & Identification',
        columns: [
          {
            Header: 'Reg. Number',
            accessor: 'registrationNumber',
            minWidth: 120
          },
          {
            Header: 'Chassis',
            accessor: 'chassisNumber',
            minWidth: 150,
            Cell: ({ value }: { value: string }) => value || 'N/A'
          },
          {
            Header: 'Reg. Year',
            accessor: 'registrationYear',
            Cell: ({ value }: { value: number }) => value || 'N/A',
            width: 90
          }
        ]
      },
      // Driver & Insurance Group
      {
        Header: 'Driver & Insurance',
        columns: [
          {
            Header: 'Driver',
            accessor: 'driver',
            minWidth: 120,
            Cell: ({ value }: { value: string }) => value || 'N/A'
          },
          {
            Header: 'Phone',
            accessor: 'driverPhone',
            minWidth: 120,
            Cell: ({ value }: { value: string }) => getPhoneNumberFromShortCodeFormat(value)
          },
          {
            Header: 'Usage',
            accessor: 'vehicleUsage',
            Cell: ({ value }: { value: string }) => {
              if (!value) return '-';
              return (
                <span className="text-xs bg-blue-100 px-2 py-1 rounded">
                  {usageMapToString[Number(value)] || value}
                </span>
              );
            },
            minWidth: 120
          },
          {
            Header: 'Value (GHS)',
            accessor: 'issuedValue',
            Cell: ({ value }: { value: number }) => (
              <span className="font-medium text-green-600">{formatCurrencyAmount(value)}</span>
            ),
            minWidth: 120
          },
          {
            Header: 'Cover Type',
            accessor: 'protectionType',
            Cell: ({ value }: { value: string }) => {
              const coverTypeMapping: { [key: string]: string } = {
                '1': 'Comprehensive',
                '2': 'Third party Fire Theft',
                '3': 'Third party',
                '4': 'Smart Drive'
              };

              return value ? (
                <span className="text-xs bg-blue-100 px-2 py-1 rounded">
                  {coverTypeMapping[value]}
                </span>
              ) : (
                '-'
              );
            },
            minWidth: 120
          }
        ]
      },
      // Actions Column
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }: { row: any }) => (
          <div className="flex items-center justify-center space-x-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                editVehicle(row.original);
              }}
              className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
              title="Edit vehicle">
              <Edit className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                updateModalStates({
                  show: true,
                  showCloseButton: false,
                  size: 'sm',
                  children: (
                    <ActionConfirmation
                      action="delete vehicle"
                      type="danger"
                      resourceType={''}
                      resourceName={''}
                      onClose={closeModal}
                      onConfirm={() => {
                        removeVehicle(row.original.registrationNumber?.trim());
                        closeModal();
                      }}
                    />
                  )
                });
              }}
              className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
              title="Remove vehicle">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
        width: 90,
        disableSortBy: true
      }
    ],
    []
  );

  const handleCSVUpload = async (file: File | null) => {
    if (!file) {
      showToast.error('No file selected');
      return;
    }

    // Check file type
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/csv'];
    const isValidType = validTypes.includes(file.type) || file.name.toLowerCase().endsWith('.csv');

    if (!isValidType) {
      showToast.error('Please upload a valid CSV file');
      return;
    }

    // Check file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      showToast.error('File size exceeds 10MB limit');
      return;
    }

    setIsUploading(true);
    setFleetCSV(file);

    try {
      const text = await file.text();
      const lines = text
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line !== ''); // Remove empty lines

      // Find the header row (look for a row containing 'make' and 'model')
      const headerRowIndex = lines.findIndex((line) => {
        const row = line.toLowerCase();
        return row.includes('make') && row.includes('model');
      });

      if (headerRowIndex === -1) {
        throw new Error('Could not find header row in CSV file');
      }

      // Read headers from the found header row
      const headers = lines[headerRowIndex].split(',').map((h) => h.trim().toLowerCase());

      // Expected headers from the CSV structure
      const expectedHeaders = [
        'make',
        'model',
        'vehiclebodytype',
        'registrationyear',
        'manufacturingyear',
        'registrationnumber',
        'issuedvalue',
        'maxseat',
        'cc',
        'chassisnumber',
        'color',
        'risktypeid',
        'portfolioid',
        'excessbought',
        'region',
        'maindriver',
        'driverphonenumber',
        'driverhascomplications',
        'vehicleingoodstateofrepair',
        'previousinsurancedeclined',
        'extratppdllimit'
      ];

      // Validate headers (case insensitive)
      const missingHeaders = expectedHeaders.filter(
        (expected) => !headers.some((header) => header.toLowerCase() === expected.toLowerCase())
      );

      if (missingHeaders.length > 0) {
        throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
      }

      // Ensure there's at least one data row after the header
      if (lines.length <= headerRowIndex + 1) {
        throw new Error('No data rows found in CSV file');
      }

      // Process the data rows starting after the header
      const newVehicles: FleetVehicle[] = [];
      const dataStartIndex = headerRowIndex + 1;

      for (let i = dataStartIndex; i < lines.length; i++) {
        const values = lines[i].split(',').map((v) => v.trim());
        if (values.length < 1 || values.every((v) => v === '')) continue; // Skip empty lines

        // Map CSV values to expected format
        const getValue = (field: string) => {
          const index = headers.findIndex((h) => h.toLowerCase() === field.toLowerCase());
          return index >= 0 && index < values.length ? values[index] : '';
        };

        // Skip if required fields are missing
        const registrationNumber = getValue('registrationnumber');
        if (!registrationNumber) {
          console.warn(`Skipping row ${i + 1}: Missing registration number`);
          continue;
        }

        // Map risk type ID to usage string
        const riskTypeId = parseInt(getValue('risktypeid'));

        // Create vehicle object with proper typing
        const vehicle: FleetVehicle = {
          maker: getValue('make'),
          model: getValue('model'),
          yearIssued: parseInt(getValue('manufacturingyear')),
          color: getValue('color'),
          chassisNumber: getValue('chassisnumber'),
          excessBought: getValue('excessbought'),
          registrationNumber: getValue('registrationnumber'),
          issuedValue: parseFloat(getValue('issuedvalue')),
          vehicleType: 'fleet',
          vehicleUsage: String(riskTypeId),
          vehicleBodyType: getValue('vehiclebodytype'),
          registrationYear: parseInt(getValue('registrationyear')),
          maxSeat: parseInt(getValue('maxseat')),
          cc: parseInt(getValue('cc')),
          protectionType: getValue('portfolioid'),
          region: getValue('region'),
          driver: getValue('maindriver'),
          driverPhone: getValue('driverphonenumber'),
          driverHasComplications: getValue('driverhascomplications'),
          isInGoodRepairState: getValue('vehicleingoodstateofrepair'),
          vehicleInGoodRepairState: getValue('vehicleingoodstateofrepair'),
          insurerPortfolioId: parseInt(getValue('portfolioid')),
          riskTypeId: riskTypeId,
          extraTppdlLimit: parseFloat(getValue('extratppdllimit')),
          previousInsuranceDeclined: getValue('previousinsurancedeclined'),

          // Required fields with defaults
          alterationDetails: '',
          driverComplications: '',
          driverEmail: '',
          driverLicense: '',
          hasInstalledAccessories: '',
          hasInterestedParties: '',
          images: '',
          installedAccessoriesDetails: '',
          installedAccessoriesValue: 0,
          interestedParties: '',
          isVehicleAltered: '',
          natureOfInterest: '',
          owner: getValue('maindriver') || '',
          ownerAddress: null,
          ownerLossPayee: '',
          ownerPhone: getValue('driverphonenumber') || '',
          repairState: '',
          state: '',
          thirdPartyInterested: '',
          vehicleAltered: '',
          vehiclePhotoData: ''
        };

        newVehicles.push(vehicle);
      }

      if (newVehicles.length > 0) {
        const updatedVehicles = newVehicles.map((v, i) => (i === 0 ? { ...v, fleetCSV: file } : v));
        setVehicles(updatedVehicles);
        onVehiclesUpdate(updatedVehicles, file);
        setCurrentStep(2);
        showToast.success(
          `${newVehicles.length} vehicle${newVehicles.length > 1 ? 's' : ''}`,
          
        );

        // Reset file input
        const fileInput = document.getElementById('fleet-csv-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      } else {
        showToast.warning('No valid vehicle data found in the CSV');
      }
    } catch (error) {
      console.error('Error processing CSV:', error);

      let errorMessage = 'Failed to process CSV file';
      if (error instanceof Error) {
        errorMessage = error.message;

        // Add more specific error messages for common issues
        if (error.message.includes('Unexpected token')) {
          errorMessage = 'Invalid CSV format. Please check the file and try again.';
        } else if (error.message.includes('Missing required columns')) {
          errorMessage = `CSV format error: ${error.message}. Please use the provided template.`;
        }
      }

      showToast.error(errorMessage);

      // Reset file input on error
      const fileInput = document.getElementById('fleet-csv-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } finally {
      setIsUploading(false);
    }
  };


  // Add new vehicle manually
  const addNewVehicle = () => {
    updateModalStates({
      show: true,
      size: '5xl',
      children: (
        <CreatePolicyFleetVehicleModal
          mode="add"
          onProceed={(newVehicle) => {
            const updatedVehicles = [...vehicles, newVehicle];
            setVehicles(updatedVehicles);
            onVehiclesUpdate(updatedVehicles, fleetCSV || undefined);
            closeModal();
          }}
          onClose={closeModal}
        />
      ),
      classes: '!p-0 r-[5px]'
    });
  };

  // Edit vehicle
  const editVehicle = (vehicleToEdit: FleetVehicle) => {
    updateModalStates({
      show: true,
      size: '5xl',
      children: (
        <CreatePolicyFleetVehicleModal
          mode="edit"
          vehicle={vehicleToEdit}
          onProceed={(updatedVehicle) => {
            // Always start with a copy of the current vehicles
            let updatedVehicles = [...vehicles];

            // Find the index of the vehicle we're updating
            const vehicleIndex = updatedVehicles.findIndex(
              (v) =>
                v.registrationNumber?.toLowerCase() ===
                vehicleToEdit.registrationNumber?.toLowerCase()
            );

            if (vehicleIndex !== -1) {
              // Update the vehicle at the found index
              updatedVehicles[vehicleIndex] = { ...updatedVehicle, fleetCSV };
            } else {
              // If vehicle not found, add it to the array
              updatedVehicles = [...updatedVehicles, updatedVehicle];
            }

            // Update local state and notify parent component
            setVehicles(updatedVehicles);
            onVehiclesUpdate(updatedVehicles, fleetCSV || undefined);
            closeModal();
          }}
          onClose={closeModal}
        />
      ),
    });
  };

  // Remove vehicle
  const removeVehicle = (registrationNumber: string) => {
    setVehicles((currentVehicles) => {
      const newVehicles = currentVehicles.filter(
        (v) => v.registrationNumber?.toLowerCase() !== registrationNumber?.toLowerCase()
      );

      const firstVehicle = newVehicles[0];
      const updatedVehicles = firstVehicle
        ? [
          { ...firstVehicle, fleetCSV: fleetCSV || firstVehicle.fleetCSV },
          ...newVehicles.slice(1)
        ]
        : newVehicles;

      onVehiclesUpdate(updatedVehicles, updatedVehicles[0]?.fleetCSV);
      showToast.success('Vehicle removed successfully');
      return updatedVehicles;
    });
  };

  // Remove all vehicles
  const removeAllVehicles = () => {
    if (
      window.confirm(
        `Are you sure you want to remove all ${vehicles.length} vehicles? This action cannot be undone.`
      )
    ) {
      const emptyArray: FleetVehicle[] = [];
      setVehicles(emptyArray);
      onVehiclesUpdate(emptyArray, undefined);
      setFleetCSV(undefined);
      showToast.success('All vehicles removed successfully');
    }
  };

  // Submit fleet policy
  const submitFleetPolicy = () => {
    if (vehicles.length < 2) {
      showToast.error('Please provide at least two vehicles');
      return;
    }

    onVehiclesUpdate(vehicles, fleetCSV || undefined);
    onNext();
  };

  const handleFileDeleted = () => {
    setFleetCSV(undefined);
    const updatedVehicles = vehicles.map((v) => ({ ...v, fleetCSV: undefined }));
    setVehicles([]);
    onVehiclesUpdate([], undefined);
    showToast.success('All vehicles deleted successfully');
  };

  // Calculate totals
  const totalVehicles = vehicles?.length;

  return (
    <div className="">


      {/* CSV Upload */}
      <div className="bg-white dark:bg-darkBg rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Upload Fleet Data</h2>

        {/* Template download */}
        <div className="mb-6 p-4 bg-primary-main/10 rounded-lg">
          <div className="flex flex-wrap gap-3 md:gap-0 items-center justify-center md:justify-between ">
            <div className='text-center md:text-start'>
              <h3 className="text-sm font-medium dark:text-white">Download CSV Template</h3>
              <p className="text-sm dark:text-gray-300">Get the template with required columns and sample data</p>
            </div>

            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
              {vehicles.length === 0 && <Button
                onClick={() => onDownloadFileFromUrl(templateUrl, templateName)}
                label="Download Template"
                icon={<Download className="h-4 w-4" />}
                className="bg-primary-main "
              />}

              {/* Manual entry option */}
              {!dataFromQuoteCalculation?.IS_FLEET_QUOTE_CALCULATOR && <Button
                onClick={addNewVehicle}
                className="px-6 py-2"
                label="Add vehicle"
                icon={<Plus className="h-4 w-4" />}
                bgColor="primary"
              />}

              {/* Remove all vehicles button */}
              {vehicles.length > 2 && (
                <Button
                  onClick={removeAllVehicles}
                  label="Remove All"
                  icon={<Trash2 className="h-4 w-4" />}
                  className="!bg-red-600 !hover:bg-red-700 text-white px-4 py-2 text-sm"
                />
              )}
            </div>
          </div>
        </div>

        {/* File upload */}
        <div className="border-2 border-dashed border-gray-300 dark:border-darkBg-border rounded-lg p-8 text-center">
          {isUploading ? (
            <Loader message="Processing CSV file..." />
          ) : (
            <div className="max-w-5xl mx-auto">
              <FileUpload
                id="fleet-csv-upload"
                type="csv"
                onFileLoad={(file: any) => {
                  if (file && file[0]) {
                    setFleetCSV(file[0]);
                    handleCSVUpload(file[0]);
                  }
                }}
                showFileMax={false}
                onFileDeleted={handleFileDeleted}
                savedImage={fleetCSV ?? undefined}
                maxHeight={90}
                maxWidth={200}
                imageContainerHeight={'200px'}
                imageAlt={'Fleet'}
                title="Upload a CSV file with your fleet vehicle information"
              />
            </div>
          )}
        </div>

        {/* Vehicles table */}
        {vehicles && vehicles.length > 0 && (
          <div className="overflow-x-auto">
            <GTable
              columns={columns}
              data={vehicles}
              classes="h-fit"
              tableType={'fleet'}
              showSearch={false}
            />

            <div className="text-sm text-gray-600 dark:text-gray-400 mt-4">
              Total Vehicles: <span className="font-semibold">{totalVehicles}</span>
            </div>
          </div>
        )}

        {/* Navigation for fleet vehicles */}
        {!dataFromQuoteCalculation?.IS_FLEET_QUOTE_CALCULATOR && <div className="flex justify-between mt-8 pt-6 border-t dark:border-darkBg-border">
          <Button onClick={onPrevious} label="Previous" icon={<ArrowLeft className="h-4 w-4" />} />

          <Button
            onClick={submitFleetPolicy}
            label="Next: Policy Details"
            bgColor="primary"
            appendIcon={<ArrowRight className="h-4 w-4" />}
          />
        </div>}
      </div>
      <ModalComponent />
    </div>
  );
};

export default PolicyFleetCreationPage;
